import { useSearchParams } from "react-router-dom";

/** The audit deep-link context carried by `auditRouteFor()` (lib/auditLinks.ts). */
export interface AuditHighlight {
  /** `r` — the id of the record the audit entry points at. */
  recordId: string | null;
  /** `a` — insert | update | delete. */
  action: string | null;
  /** `f` — the DB column names changed by the audit entry. */
  fields: string[];
  /** `ar` — the audit_log id of the entry that produced the link. */
  auditId: number | null;
  /** `e` — the DB table name the audit entry points at. */
  entityType: string | null;
}

const EMPTY: AuditHighlight = {
  recordId: null,
  action: null,
  fields: [],
  auditId: null,
  entityType: null,
};

/**
 * Parse the audit deep-link params from the URL. Self-contained on purpose —
 * any component under the router (pages, dialogs, the shared `Field`) can call
 * it without a provider being mounted.
 */
export function useAuditHighlight(): AuditHighlight {
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get("r");
  const action = searchParams.get("a");
  const rawFields = searchParams.get("f");
  const auditId = searchParams.get("ar");
  const entityType = searchParams.get("e");
  if (!recordId && !action && !rawFields && !auditId && !entityType) return EMPTY;
  return {
    recordId,
    action,
    fields: rawFields
      ? rawFields
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean)
      : [],
    auditId: auditId !== null && auditId !== "" ? Number(auditId) : null,
    entityType,
  };
}

/** True when `field` is one of the fields changed by the linked audit entry. */
export function isHighlightedField(highlight: AuditHighlight | null, field?: string): boolean {
  return Boolean(
    highlight && field && highlight.fields.length > 0 && highlight.fields.includes(field),
  );
}

/** True when the deep link points at a deleted record (ghost display). */
export function isDeletedRecord(highlight: AuditHighlight | null): boolean {
  return highlight?.action === "delete" && highlight.recordId !== null;
}